/** App-root error boundary. Without one, ANY render crash unmounts the whole
 * tree and the user sees a blank page. Worse, the view-restore feature reopens
 * the last page/record on every load — so a crash while rendering a restored
 * detail becomes a permanent loop (blank on every refresh, no escape). This
 * catches the crash, CLEARS the saved view so the next load starts clean, and
 * shows the error + a Reload button instead of a blank screen. */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface State { error: Error | null; info: string }

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, info: "" };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Break any restore-induced crash loop: drop the saved view so a reload
    // (or the next refresh) comes up on the default page instead of re-opening
    // the record that just crashed.
    try { localStorage.removeItem("crm2_view"); } catch { /* ignore */ }
    this.setState({ info: info.componentStack ?? "" });
    // eslint-disable-next-line no-console
    console.error("[app] render crash — saved view cleared so a reload recovers", error, info);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#0b1220", color: "#e2e8f0", fontFamily: "system-ui, sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 560, width: "100%", background: "#111a2e", border: "1px solid #1e293b", borderRadius: 14, padding: "22px 24px" }}>
          <h1 style={{ fontSize: 18, margin: "0 0 8px" }}>Something went wrong</h1>
          <p style={{ fontSize: 13.5, color: "#94a3b8", margin: "0 0 16px", lineHeight: 1.5 }}>
            The app hit an error while loading and reset your last view. Reload to continue — you'll come back to the main list.
          </p>
          <button
            onClick={() => { try { location.reload(); } catch { /* ignore */ } }}
            style={{ background: "#2563eb", color: "#fff", border: 0, borderRadius: 9, padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
          >Reload</button>
          <pre style={{ marginTop: 16, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11.5, color: "#f87171", background: "#0b1220", border: "1px solid #1e293b", borderRadius: 8, padding: "10px 12px", maxHeight: 200, overflow: "auto" }}>
            {String(error.message || error)}
          </pre>
        </div>
      </div>
    );
  }
}
